/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { Popup } from '@progress/kendo-react-popup';
import TextValueComp from '../GenericMultiSelect/TextValueComp';
import { BasicSelect } from '../../BasicSelect/BasicSelect';
function GroupSelector({ groupedOptions, selectedValues, onGroupSelect, onReset }) {
    const buttonStyle = { justifyContent: 'space-between' };
    const counterStyle = {
        marginRight: '2rem',
        background: '#F0EDF8',
        color: '#3D1B9D',
        boxSizing: 'border-box',
        padding: '.2rem',
        borderRadius: '20%'
    };
    const getTotalSelectionsInGroup = (group) => {
        return group.optionsForSelect.filter((option) => selectedValues.includes(option.value)).length;
    };
    const totalSelections = selectedValues.length;
    return (React.createElement("div", { className: "qmb-popup--actions--y-max" },
        React.createElement("div", { className: "popup__section--y-fill" }, groupedOptions.map((group, index) => {
            const selectionsInGroup = getTotalSelectionsInGroup(group);
            return (React.createElement("button", { key: `group-${index}`, className: "qmb-control--menu", type: "button", style: buttonStyle, onClick: (e) => onGroupSelect(index, e.currentTarget) },
                React.createElement("span", null, group.label),
                React.createElement("span", { style: counterStyle }, selectionsInGroup > 0 ? selectionsInGroup : null)));
        })),
        totalSelections > 0 && (React.createElement(React.Fragment, null,
            React.createElement("hr", { className: "popup__split" }),
            React.createElement("div", { className: "popup__section" },
                React.createElement("ul", { role: "presentation" },
                    React.createElement("li", null,
                        React.createElement("div", { className: "messageContainer" },
                            React.createElement("button", { type: "button", className: "qmb-control--sm--reset", onClick: () => onReset() },
                                React.createElement("i", { className: "fa-light fa-arrow-rotate-left" }),
                                "Reset")))))))));
}
function GroupOptionsSelector({ group, selectedValues, single, disableSorting, onChange }) {
    const [searchText, setSearchText] = useState('');
    const handleSelectionChange = (values) => {
        onChange(values);
    };
    const handleSearchTextChange = (text) => {
        setSearchText(text);
    };
    const handleReset = () => {
        // Only remove values that belong to this specific group
        const groupValues = group.optionsForSelect.map((option) => option.value);
        const filteredValues = selectedValues.filter((value) => !groupValues.includes(value));
        onChange(filteredValues);
    };
    return (React.createElement(BasicSelect, { name: "group-options-selector", value: selectedValues, placeholder: group.placeholder, fixedOptions: group.optionsForSelect, disableSorting: disableSorting, searchText: searchText, single: single, allowLetterAvatar: !!group.allowLetterAvatar, onChange: handleSelectionChange, onChangeSearchText: handleSearchTextChange, onReset: handleReset }));
}
export default function GroupedMultiSelect({ label, value = [], groupedOptions = [], disableSorting = false, single = false, onChange }) {
    const rootAnchor = useRef(null);
    const popup = useRef(null);
    const subPopup = useRef(null);
    const [open, setOpen] = useState(false);
    const [activeGroupIndex, setActiveGroupIndex] = useState(null);
    const [activeGroupAnchor, setActiveGroupAnchor] = useState(null);
    const [isSubanchorReady, setIsSubanchorReady] = useState(false);
    useEffect(() => {
        if (open && popup.current && activeGroupIndex !== null) {
            setIsSubanchorReady(true);
        }
        else {
            setIsSubanchorReady(false);
        }
    }, [open, popup.current, activeGroupIndex]);
    const onClickAway = (e) => {
        if (e.target !== rootAnchor.current) {
            setActiveGroupIndex(null);
            setActiveGroupAnchor(null);
            setOpen(false);
        }
    };
    const allOptions = useMemo(() => {
        return groupedOptions.flatMap((group) => group.optionsForSelect);
    }, [groupedOptions]);
    const textValue = useMemo(() => {
        // Check if any group has allowLetterAvatar enabled for the global display
        const hasAnyLetterAvatar = groupedOptions.some((group) => group.allowLetterAvatar);
        return (value === null || value === void 0 ? void 0 : value.length) > 0 ? (React.createElement(TextValueComp, { allowLetterAvatar: hasAnyLetterAvatar, internalValue: value, options: allOptions })) : (`${label}`);
    }, [value, allOptions, label, groupedOptions]);
    const handleGroupSelect = (groupIndex, buttonRef) => {
        setActiveGroupIndex(groupIndex);
        setActiveGroupAnchor(buttonRef);
    };
    const handleReset = () => {
        onChange([]);
        setOpen(false);
        setActiveGroupIndex(null);
        setActiveGroupAnchor(null);
    };
    const activeGroup = activeGroupIndex !== null ? groupedOptions[activeGroupIndex] : null;
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { className: `qmb-control--menu multiSelectInput ${(value === null || value === void 0 ? void 0 : value.length) === 0 ? 'items-center' : ''}`, type: "button", ref: rootAnchor, onClick: () => {
                setOpen(!open);
                setActiveGroupIndex(null);
                setActiveGroupAnchor(null);
            } }, textValue),
        (value === null || value === void 0 ? void 0 : value.length) > 0 && (React.createElement("label", { className: "qmb-label multiSelectInputLabel" },
            label,
            " ",
            !single && React.createElement("b", null, value === null || value === void 0 ? void 0 : value.length))),
        React.createElement(Popup, { anchor: rootAnchor.current, show: open, ref: popup, id: "grouped-multiselect-parent-popup" },
            React.createElement(ClickAwayListener, { onClickAway: onClickAway },
                React.createElement("div", null,
                    React.createElement(GroupSelector, { groupedOptions: groupedOptions, selectedValues: value, onGroupSelect: handleGroupSelect, onReset: handleReset }),
                    isSubanchorReady && activeGroup && activeGroupAnchor && (React.createElement(Popup, { ref: subPopup, anchor: activeGroupAnchor, show: activeGroupIndex !== null, anchorAlign: { horizontal: 'right', vertical: 'top' }, popupAlign: { horizontal: 'left', vertical: 'top' } },
                        React.createElement(GroupOptionsSelector, { group: activeGroup, selectedValues: value, single: single, disableSorting: disableSorting, onChange: onChange }))))))));
}
